/**
 * Created by fatih.kahraman on 02/04/2020.
 */
import { insertTrackEvent } from '@comeon/analytics';
import reducerRegistry from '@comeon/reducer-registry';
import formatLocaleForZendeskDepartment from '~/utils/format-locale-for-zendesk.js';
import zendeskDepartments from '~/components/support/zendesk-departments.js';
import { importAda, openAdaChat } from '~/components/support/ducks/index-ada.js';
import {
	SHOW_DIALOG_BOX,
	SUPPORT_ADA_REMOVE_CUSTOM_METAFIELD,
	SUPPORT_ADA_UPDATE_CUSTOM_METAFIELD,
	SUPPORT_VIP_TAG_CV_VALUE,
	TOGGLE_SUPPORT_LOADING_STATUS
} from '~/components/support/ducks/constants.js';
import { importDixa, openDixaChat } from '~/components/support/ducks/index-dixa';

const REDUCER_NAME = 'support';

/**
 * Function to Forcefully open the zendesk widget
 */
const openZendesk = (closeHandler = undefined) => {
	window.zE(() => {
		window.zE.show();
		window.zE('webWidget', 'open');
		window.zE('webWidget:on', 'close', () => {
			if (closeHandler) {
				closeHandler();
			}
		});
	});
};

const toggleSupportLoadingStatus = loadingStatus => ({
	type: TOGGLE_SUPPORT_LOADING_STATUS,
	payload: loadingStatus
});

const setVIPChatTagCVValue = cvValue => ({
	type: SUPPORT_VIP_TAG_CV_VALUE,
	payload: cvValue
});

/**
 * @param {string} name
 * @param {string} email
 * Function to Prefill contact us form with Logged in user's name and email
 */
const preFillForm = (name, email) => {
	window.zE('webWidget', 'prefill', {
		name: {
			value: name
		},
		email: {
			value: email
		}
	});
};

/**
 * @param {string} name
 * @param {string} email
 * Function to identify Logged in user's name and email to zendesk
 */
const identifyUser = (name, email) => {
	window.zE('webWidget', 'identify', {
		name,
		email
	});
};

const prefillFormAndIdentifyUser = ({ authenticated, email, firstName, lastName }) => {
	if (authenticated) {
		const zendeskName = firstName || lastName ? `${firstName} ${lastName}` : `Anonymous User`;
		const zendeskEmail = email || ``;
		identifyUser(zendeskName, zendeskEmail);
		preFillForm(zendeskName, zendeskEmail);
	}
};

/**
 * @param {object} config
 * @param {string} config.search
 * @param {string} config.label
 * Function to show initial search results for when the user first opens the support widget
 */
const setHelpCenterSuggestions = config => {
	window.zE.setHelpCenterSuggestions({ ...config });
};

/**
 * @param {string} countryCode
 * Function to set locale for Zendesk widget
 */
const setLocale = (countryCode = 'en_XX') => {
	const localeMapForZendesk = {
		en_XX: 'en-us',
		en_EU: 'en-us',
		en_IN: 'en-in',
		da_DK: 'da',
		de_DE: 'de',
		es_CL: 'es',
		fi_FI: 'fi',
		no_NO: 'no',
		pl_PL: 'pl',
		sv_GU: 'sv',
		sv_SE: 'sv',
		en_NZ: 'en-nz',
		fi_GU: 'fi',
		ja_JP: 'ja'
	};
	const zendeskLocaleValue = localeMapForZendesk[countryCode] ? localeMapForZendesk[countryCode] : 'en-us';
	window.zE('webWidget', 'setLocale', zendeskLocaleValue);
};

/**
 * Function to check for widget support on window level Zendesk widget
 */
const checkIfWidgetExists = () => {
	if (
		window.zE &&
		(document.getElementsByClassName('zEWidget-launcher').length > 0 ||
			document.getElementById('webWidget') ||
			document.getElementById('launcher'))
	) {
		return true;
	}
	return false;
};

const getDefaultDepartment = (siteCode, locale) => {
	let department;

	if (zendeskDepartments?.[siteCode]?.[locale]?.department) {
		department = zendeskDepartments[siteCode][locale].department;
	}

	return department;
};

const setDepartment = departmentName => {
	window.zE('webWidget', 'updateSettings', {
		webWidget: {
			chat: {
				departments: {
					enabled: [],
					select: departmentName
				}
			}
		}
	});
	insertTrackEvent({
		category: 'Zendesk',
		action: 'DepartmentSetChat',
		label: departmentName
	});
};

const getDepartmentStatus = department => {
	const departmentStatus = window.zE('webWidget:get', 'chat:department', department);

	return departmentStatus?.status;
};

const suppressChat = bool => {
	window.zE('webWidget', 'updateSettings', {
		webWidget: {
			chat: {
				suppress: bool
			}
		}
	});
};

const suppressHelpCenter = bool => {
	window.zE('webWidget', 'updateSettings', {
		webWidget: {
			helpCenter: {
				suppress: bool
			}
		}
	});
};

const toggleContactOptions = bool => {
	window.zE('webWidget', 'updateSettings', {
		webWidget: {
			contactOptions: {
				enabled: bool
			}
		}
	});
};

const getChatTag = (siteCode, locale) => {
	return zendeskDepartments?.[siteCode]?.[locale]?.tag;
};

const setChatTags = tags => {
	window.zE('webWidget', 'chat:addTags', tags);
};

const removeChatTags = tags => {
	window.zE('webWidget', 'chat:removeTags', tags);
};

// showSupportDialogBox injected to break circular dependency
const initializeZendeskConfig = showSupportDialogBox => callbackFn => {
	return async (dispatch, getState) => {
		try {
			window.zE('webWidget', 'updateSettings', {
				webWidget: {
					navigation: {
						popoutButton: {
							enabled: false
						}
					},
					chat: {
						departments: {
							enabled: []
						}
					},
					contactOptions: {
						enabled: true
					},
					helpCenter: {
						originalArticleButton: false
					},
					offset: {
						horizontal: window.location.search.includes('sidebar') ? '379px' : '4px',
						vertical: '14px',
						mobile: {
							horizontal: '-9px',
							vertical: '80px'
						}
					},
					zIndex: 32
				}
			});

			window.zE('webWidget', 'hide');

			window.zE('webWidget:on', 'open', () => {
				const {
					authentication: { authenticated },
					account: { email, firstName, lastName },
					brand: { regHeaderEnabled }
				} = getState();

				// before we open the chat window
				// we'll check if there is a regulation header
				// and resize chat window accordingly
				setTimeout(() => {
					const elem = document.querySelector('#webWidget');
					if (elem) {
						if (regHeaderEnabled) {
							elem.className = 'hasHeader';
						} else {
							elem.className = '';
						}
					}
				}, 500);

				prefillFormAndIdentifyUser({
					authenticated,
					email,
					firstName,
					lastName
				});
			});
			window.zE('webWidget:on', 'close', () => {
				dispatch(showSupportDialogBox(false));
			});

			window.zE('webWidget:on', 'chat:connected', () => {
				const {
					account: { locale, birth },
					brand: { siteCode, isNetherlandsRegulatedFranchise },
					support: { supportVIPTagCVValue, supportVIPTagEnabled },
					authentication: { authenticated }
				} = getState();

				const formattedLocale = formatLocaleForZendeskDepartment(locale);
				setLocale(formattedLocale);

				let departmentName = getDefaultDepartment(siteCode, formattedLocale);

				if (supportVIPTagEnabled && supportVIPTagCVValue >= 10 && supportVIPTagCVValue <= 90) {
					departmentName += ' 2';
				}

				insertTrackEvent({
					category: 'Zendesk',
					action: 'ZendeskSiteCodeCheck',
					label: siteCode
				});
				insertTrackEvent({
					category: 'Zendesk',
					action: 'ZendeskLocaleCheck',
					label: formattedLocale
				});
				insertTrackEvent({
					category: 'Zendesk',
					action: 'DepartmentSet',
					label: departmentName
				});

				const chatTags = [];
				// Checking for User 24 Years regulation here

				if (isNetherlandsRegulatedFranchise && authenticated && birth) {
					const today = new Date();
					const birthDate = new Date(birth.split('T')[0]);
					// const birthDate = new Date(birth.slice(0, -3));
					let age = today.getFullYear() - birthDate.getFullYear();
					const m = today.getMonth() - birthDate.getMonth();
					if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
						age -= 1;
					}
					if (age <= 24) {
						chatTags.push('playerUnder24');
					}
				}

				if (getChatTag(siteCode, formattedLocale)) {
					chatTags.push(getChatTag(siteCode, formattedLocale));
				}

				if (supportVIPTagEnabled && supportVIPTagCVValue >= 10 && supportVIPTagCVValue <= 90) {
					chatTags.push(supportVIPTagCVValue.toString());
				}

				// Adding tags to the chat
				if (chatTags.length > 0) {
					setChatTags(chatTags);
				}

				const departmentStatus = getDepartmentStatus(departmentName);

				if (departmentStatus === 'online') {
					setDepartment(departmentName);
					suppressChat(false);
				} else {
					suppressChat(true);
				}

				/* suppress or enable the chat when the department goes offline or online */
				window.zE('webWidget:on', 'chat:departmentStatus', dept => {
					if (dept.name === departmentName) {
						if (dept.status === 'online') {
							suppressChat(false);
						} else if (dept.status === 'offline') {
							suppressChat(true);
						}
					}
				});
				if (callbackFn) {
					callbackFn();
				}
			});
		} catch (err) {
			// console.log(err);
		}
	};
};

// showSupportDialogBox injected to break circular dependency
const importZendeskFactory = showSupportDialogBox => callbackFn => {
	return async (dispatch, getState) => {
		const {
			support: {
				supportLoading,
				supportLoaded,
				zendesk: { zendeskKey }
			}
		} = getState();

		if (supportLoading) {
			// nevermind
		} else if (supportLoaded) {
			if (callbackFn) {
				callbackFn();
			}
		} else if (!supportLoading && !supportLoaded) {
			dispatch(
				toggleSupportLoadingStatus({
					loading: true,
					loaded: false
				})
			);

			const zendeskUrl = `//static.zdassets.com/ekr/snippet.js?key=${zendeskKey}`;
			const script = document.createElement('script');
			script.src = zendeskUrl;
			script.async = true;
			script.type = 'text/javascript';
			script.id = 'ze-snippet';
			script.onload = () => {
				const detectionInterval = setInterval(() => {
					if (checkIfWidgetExists()) {
						dispatch(initializeZendeskConfig(showSupportDialogBox)(callbackFn));
						clearInterval(detectionInterval);
						dispatch(
							toggleSupportLoadingStatus({
								loading: false,
								loaded: true
							})
						);
					}
				}, 100);
			};
			document.getElementsByTagName('head')[0].appendChild(script);
		}
	};
};

const resetZendesk = () => {
	if (window.zE) {
		window.zE('webWidget', 'logout');
	}
};

const resetSupportWidget = () => {
	return (dispatch, getState) => {
		// resetting the Support VIP Tag value
		dispatch(setVIPChatTagCVValue(0));
		const { zendesk } = getState().support;

		if (zendesk) {
			resetZendesk();
		}
	};
};

const showSupportDialogBox = (bool, callback) => {
	return async (dispatch, getState) => {
		const {
			brand: { regHeaderEnabled },
			browser: {
				is: { small }
			},
			support: { ada, zendesk, dixa, supportLoading, supportLoaded }
		} = getState();

		if (ada) {
			if (!supportLoading && !supportLoaded) {
				dispatch(toggleSupportLoadingStatus({ loading: true, loaded: false }));
			}
			const { botHandle } = ada;
			const { locale } = getState().account;
			importAda(
				botHandle,
				locale.slice(0, 2),
				() => {
					dispatch(
						toggleSupportLoadingStatus({
							loading: false,
							loaded: true
						})
					);
					openAdaChat(bool, regHeaderEnabled && small);
					if (callback) {
						callback();
					}
				},
				isChatOpen => {
					dispatch({
						type: SHOW_DIALOG_BOX,
						payload: isChatOpen
					});
				}
			);
		} else if (zendesk) {
			dispatch(
				importZendeskFactory(showSupportDialogBox)(() => {
					if (bool) {
						openZendesk();
					} else {
						window.zE('webWidget', 'hide');
					}

					dispatch({
						type: SHOW_DIALOG_BOX,
						payload: bool
					});
					if (callback) {
						callback();
					}
				})
			);
		} else if (dixa) {
			if (!supportLoading && !supportLoaded) {
				dispatch(toggleSupportLoadingStatus({ loading: true, loaded: false }));
			}
			importDixa(
				dixa.messengerToken,
				() => {
					dispatch(toggleSupportLoadingStatus({ loading: false, loaded: true }));
				},
				isChatOpen => {
					dispatch({
						type: SHOW_DIALOG_BOX,
						payload: isChatOpen
					});
				},
				() => {
					openDixaChat(bool);
				}
			);
		}
	};
};

const importZendesk = importZendeskFactory(showSupportDialogBox);

const reducer = (state = {}, { type, payload }) => {
	switch (type) {
		case SHOW_DIALOG_BOX:
			return {
				...state,
				showDialogBox: payload
			};
		case TOGGLE_SUPPORT_LOADING_STATUS:
			return {
				...state,
				supportLoading: payload.loading,
				supportLoaded: payload.loaded
			};
		case SUPPORT_VIP_TAG_CV_VALUE:
			return {
				...state,
				supportVIPTagCVValue: payload
			};
		case SUPPORT_ADA_UPDATE_CUSTOM_METAFIELD:
			return {
				...state,
				ada: {
					...state.ada,
					customMetafields: {
						...state.ada.customMetafields,
						[payload.name]: payload.value
					}
				}
			};
		case SUPPORT_ADA_REMOVE_CUSTOM_METAFIELD:
			return {
				...state,
				ada: {
					...state.ada,
					customMetafields: {
						...state.ada.customMetafields,
						[payload.name]: ''
					}
				}
			};
		default:
			return state;
	}
};

reducerRegistry.register(REDUCER_NAME, reducer);

export default reducer;

export {
	importZendesk,
	getDefaultDepartment,
	setDepartment,
	getDepartmentStatus,
	setChatTags,
	removeChatTags,
	suppressChat,
	suppressHelpCenter,
	setHelpCenterSuggestions,
	toggleContactOptions,
	showSupportDialogBox,
	toggleSupportLoadingStatus,
	setVIPChatTagCVValue,
	resetZendesk,
	resetSupportWidget
};
